import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faChevronLeft,
  faChevronRight,
  faEye,
  faFilter,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
} from "@themesberg/react-bootstrap";
import {
  adminGetEarningStatements,
  adminGetEarningStatementById,
  adminProcessEarningStatementFlush,
  adminGetInactiveFlushQueue,
  adminGetEarningStatementFlushHistory,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const INACTIVE_BUTTON_STYLE = {
  background: "#ffffff",
  border: "1px solid #d8e1f0",
  borderRadius: "0.5rem",
  color: "#1f2a44",
};

const DEFAULT_PAGINATION = {
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
};

const DEFAULT_PER_PAGE = 10;

const VIEW_OPTIONS = [
  {
    key: "all",
    label: "All Statements",
    description: "All earning statements are shown below.",
  },
  {
    key: "queue",
    label: "Flush Queue",
    description: "Accounts pending flush are shown below.",
  },
  {
    key: "history",
    label: "Flush History",
    description: "Past flushed transactions are shown below.",
  },
];

const formatCurrency = (value) => {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination) => {
  const total = Number(pagination?.total || 0);
  const perPage = Number(pagination?.per_page || DEFAULT_PER_PAGE);
  const current = Math.max(1, Number(pagination?.current_page || 1));

  if (total === 0) return { start: 0, end: 0 };

  const start = (current - 1) * perPage + 1;
  const end = Math.min(current * perPage, total);

  return { start, end };
};

const getFlushBadgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "flushed") return "success";
  if (s === "pending_flush") return "warning";
  if (s === "na") return "danger";
  return "info";
};

const getUserBadgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "inactive") return "danger";
  return "secondary";
};

const labelize = (value) =>
  String(value ?? "N/A")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getRowsFromResponse = (apiData, viewKey) => {
  if (viewKey === "queue") {
    return Array.isArray(apiData?.queue) ? apiData.queue : [];
  }

  if (viewKey === "history") {
    return Array.isArray(apiData?.history) ? apiData.history : [];
  }

  return Array.isArray(apiData?.statements) ? apiData.statements : [];
};

const normalizeRow = (item) => ({
  id: item?.id ?? item?.earning_statement_id ?? null,
  statement_id: item?.earning_statement_id ?? item?.statement_id ?? item?.id ?? null,
  user: item?.user ?? "N/A",
  cycle: item?.cycle ?? "N/A",
  gross_earning: item?.gross_earning ?? 0,
  adjustments: item?.adjustments ?? 0,
  net_earning: item?.net_earning ?? 0,
  user_status: item?.user_status ?? "N/A",
  flush_status: item?.flush_status ?? "N/A",
  flush_date: item?.flush_date ?? item?.flushed_at ?? "N/A",
  finance_reference: item?.finance_reference ?? "N/A",
  remarks: item?.remarks ?? "N/A",
  raw: item,
});

const normalizeDetail = (item) => ({
  id: item?.id ?? item?.earning_statement_id ?? null,
  statement_id: item?.earning_statement_id ?? item?.statement_id ?? item?.id ?? null,
  user: item?.user ?? "N/A",
  cycle: item?.cycle ?? "N/A",
  gross_earning: item?.gross_earning ?? 0,
  adjustments: item?.adjustments ?? 0,
  net_earning: item?.net_earning ?? 0,
  user_status: item?.user_status ?? "N/A",
  flush_status: item?.flush_status ?? "N/A",
  flush_date: item?.flush_date ?? item?.flushed_at ?? "N/A",
  finance_reference: item?.finance_reference ?? "N/A",
  remarks: item?.remarks ?? "N/A",
});

export default function AdminEarningStatement() {
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [flushLoadingId, setFlushLoadingId] = useState(null);

  const [activeView, setActiveView] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [statements, setStatements] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [companyWalletBalance, setCompanyWalletBalance] = useState(null);

  const [selectedStatement, setSelectedStatement] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const searchDebounceRef = useRef(null);
  const skipPageFetchRef = useRef(false);
  const skipSearchFetchRef = useRef(false);
  const isInitialSearchRef = useRef(true);
  const activeViewRef = useRef(activeView);
  const searchTermRef = useRef(searchTerm);

  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const activeViewConfig = useMemo(
    () => VIEW_OPTIONS.find((item) => item.key === activeView) || VIEW_OPTIONS[0],
    [activeView]
  );

  const fetchStatements = useCallback(
    async ({ viewKey = "all", pageNo = 1, perPage = DEFAULT_PER_PAGE, search = "" } = {}) => {
      try {
        setLoading(true);

        const params = {
          page: pageNo,
          per_page: perPage,
        };

        const trimmedSearch = String(search || "").trim();
        if (trimmedSearch) {
          params.search = trimmedSearch;
        }

        let response;

        if (viewKey === "queue") {
          response = await adminGetInactiveFlushQueue(params);
        } else if (viewKey === "history") {
          response = await adminGetEarningStatementFlushHistory(params);
        } else {
          response = await adminGetEarningStatements(params);
        }

        const apiData = response?.data?.data || {};
        const rows = getRowsFromResponse(apiData, viewKey).map(normalizeRow);
        const apiPagination = apiData?.pagination || {};

        setStatements(rows);
        setCompanyWalletBalance(apiData?.company_wallet_balance ?? null);
        setPagination({
          current_page: Number(apiPagination?.current_page || pageNo),
          last_page: Number(apiPagination?.last_page || 1),
          per_page: Number(apiPagination?.per_page || perPage),
          total: Number(apiPagination?.total || rows.length || 0),
        });
      } catch (error) {
        console.error("Failed to fetch earning statements:", error);
        toast.error(error?.response?.data?.message || "Unable to load earning statements.");
        setStatements([]);
        setCompanyWalletBalance(null);
        setPagination(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (skipPageFetchRef.current) {
      skipPageFetchRef.current = false;
      return;
    }

    fetchStatements({
      viewKey: activeView,
      pageNo: currentPage,
      perPage: DEFAULT_PER_PAGE,
      search: searchTermRef.current,
    });
  }, [activeView, currentPage, fetchStatements]);

  useEffect(() => {
    if (skipSearchFetchRef.current) {
      skipSearchFetchRef.current = false;
      return;
    }

    if (isInitialSearchRef.current) {
      isInitialSearchRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      skipPageFetchRef.current = true;
      setCurrentPage(1);

      fetchStatements({
        viewKey: activeViewRef.current,
        pageNo: 1,
        perPage: DEFAULT_PER_PAGE,
        search: searchTerm,
      });
    }, 450);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, fetchStatements]);

  const handleViewChange = (viewKey) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    skipPageFetchRef.current = true;
    skipSearchFetchRef.current = true;

    setActiveView(viewKey);
    setCurrentPage(1);
    setSearchTerm("");
    searchTermRef.current = "";

    fetchStatements({
      viewKey,
      pageNo: 1,
      perPage: DEFAULT_PER_PAGE,
      search: "",
    });
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleOpenDetail = async (item) => {
    const statementId = item?.statement_id ?? item?.id;

    try {
      setShowDetail(true);
      setSelectedStatement(null);

      if (activeView === "all" && statementId) {
        setDetailLoading(true);
        const response = await adminGetEarningStatementById(statementId);
        const detail = response?.data?.data || null;
        setSelectedStatement(detail ? normalizeDetail(detail) : normalizeDetail(item));
        console.log("Fetched statement setSelectedStatement:", selectedStatement);
      } else {
        setSelectedStatement(normalizeDetail(item));
      }
    } catch (error) {
      console.error("Failed to fetch statement detail:", error);
      toast.error(error?.response?.data?.message || "Unable to load statement detail.");
      setSelectedStatement(normalizeDetail(item));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleProcessFlush = async (item) => {
    const statementId = item?.statement_id ?? item?.id;

    if (!statementId) {
      toast.error("Statement ID not found.");
      return;
    }

    try {
      setFlushLoadingId(statementId);
      await adminProcessEarningStatementFlush(statementId);
      toast.success("Flush processed successfully.");
      await fetchStatements({
        viewKey: activeView,
        pageNo: currentPage,
        perPage: DEFAULT_PER_PAGE,
        search: searchTermRef.current,
      });
    } catch (error) {
      console.error("Failed to process flush:", error);
      toast.error(error?.response?.data?.message || "Unable to process flush.");
    } finally {
      setFlushLoadingId(null);
    }
  };

  const { start, end } = getShowingRange(pagination);
  const pageItems = getPageItems(pagination.current_page || 1, pagination.last_page || 1);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-4">
        <div>
          <h2 className="h4 mb-1">Earning Statements</h2>
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto earning-view-tabs">
          {VIEW_OPTIONS.map((item) => (
            <Button
              key={item.key}
              type="button"
              style={activeView === item.key ? ACTIVE_BUTTON_STYLE : INACTIVE_BUTTON_STYLE}
              className="fw-semibold px-3 earning-view-tab-btn"
              onClick={() => handleViewChange(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      {/* <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <Row className="g-3 align-items-end justify-content-end">
            <Col xs={12}>
              <div className="text-muted small">
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                {activeViewConfig.description}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card> */}

      <Card className="border-0 shadow-sm rounded-4 mb-3">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div className="text-muted small mb-1">Company Wallet Balance</div>
              <div className="h4 mb-0">₹ {formatCurrency(companyWalletBalance)}</div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 earning-table-header">
            <div>
              <h5 className="mb-1">{activeViewConfig.label}</h5>
              {/* <div className="text-muted small">{activeViewConfig.description}</div> */}
            </div>

            <div className="earning-search-wrap ms-auto">
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={"Search"}
                className="earning-search-input"
              />
            </div>

            {/* <Button
              type="button"
              variant="outline-primary"
              onClick={() =>
                fetchStatements({
                  viewKey: activeView,
                  pageNo: currentPage,
                  perPage: DEFAULT_PER_PAGE,
                })
              }
              className="fw-semibold"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faRotate} className="me-2" />
              Refresh
            </Button> */}
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0 earning-statement-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Cycle</th>
                  <th>Gross Earning</th>
                  {/* <th>Adjustments</th> */}
                  <th>Net Earning</th>
                  <th>User Status</th>
                  <th>Flush Status</th>
                  <th>Flush Date</th>
                  {/* <th>Finance Ref.</th>
                  <th>Remarks</th> */}
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      Loading earning statements...
                    </td>
                  </tr>
                ) : statements.length > 0 ? (
                  statements.map((item) => {
                    const rowId = item.statement_id ?? item.id;
                    const showProcessFlush = activeView === "all";
                    const isFlushDisabled = String(item.flush_status || "").toLowerCase() === "flushed";

                    return (
                      <tr key={rowId}>
                        <td className="fw-semibold">{item.user || "N/A"}</td>
                        <td>{item.cycle || "N/A"}</td>
                        <td>₹ {formatCurrency(item.gross_earning)}</td>
                        {/* <td>₹ {formatCurrency(item.adjustments)}</td> */}
                        <td className="fw-semibold">₹ {formatCurrency(item.net_earning)}</td>
                        <td>
                          <Badge
                            bg={getUserBadgeVariant(item.user_status)}
                            className="rounded-pill px-3 py-2 text-capitalize"
                          >
                            {labelize(item.user_status)}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            bg={getFlushBadgeVariant(item.flush_status)}
                            className="rounded-pill px-3 py-2 text-capitalize"
                          >
                            {labelize(item.flush_status)}
                          </Badge>
                        </td>
                        <td>{formatDate(item.flush_date)}</td>
                        {/* <td className="text-break">{item.finance_reference || "N/A"}</td>
                        <td className="text-break">{item.remarks || "N/A"}</td> */}
                        <td className="text-center">
                          <div className="d-flex justify-content-end gap-2 flex-nowrap">
                            {showProcessFlush ? (
                              <Button
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                className="fw-semibold d-inline-flex align-items-center gap-2 text-nowrap p-2"
                                onClick={() => handleProcessFlush(item)}
                                disabled={flushLoadingId === rowId || isFlushDisabled}
                              >
                                <FontAwesomeIcon icon={faArrowsRotate} />
                                {/* {flushLoadingId === rowId ? "Processing..." : "Process Flush"} */}
                              </Button>
                            ) : null}

                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="fw-semibold d-inline-flex align-items-center gap-2 text-nowrap p-2"
                              onClick={() => handleOpenDetail(item)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                              {/* View */}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      No earning statements found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => {
                  setCurrentPage((prev) => Math.max(1, prev - 1));
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {pageItems.map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => {
                      setCurrentPage(item);
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => {
                  setCurrentPage((prev) => Math.min(pagination.last_page, prev + 1));
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Earning Statement Detail</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detailLoading ? (
            <div className="text-center text-muted py-4">Loading statement detail...</div>
          ) : selectedStatement ? (
            <Row className="g-3">
              {/* <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Statement ID</div>
                    <div className="fw-bold">{selectedStatement.statement_id || selectedStatement.id || "N/A"}</div>
                  </Card.Body>
                </Card>
              </Col> */}

              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">User</div>
                    <div className="fw-bold">{selectedStatement.user || "N/A"}</div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Cycle</div>
                    <div className="fw-bold">{selectedStatement.cycle || "N/A"}</div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Flush Date</div>
                    <div className="fw-bold">{formatDate(selectedStatement.flush_date)}</div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Gross Earning</div>
                    <div className="fw-bold">₹ {formatCurrency(selectedStatement.gross_earning)}</div>
                  </Card.Body>
                </Card>
              </Col>

              {/* <Col xs={12} md={4}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Adjustments</div>
                    <div className="fw-bold">₹ {formatCurrency(selectedStatement.adjustments)}</div>
                  </Card.Body>
                </Card>
              </Col> */}

              <Col xs={12} md={4}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Net Earning</div>
                    <div className="fw-bold">₹ {formatCurrency(selectedStatement.net_earning)}</div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={4}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">User Status</div>
                    <Badge
                      bg={getUserBadgeVariant(selectedStatement.user_status)}
                      className="rounded-pill px-3 py-2 text-capitalize"
                    >
                      {labelize(selectedStatement.user_status)}
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={4}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Flush Status</div>
                    <Badge
                      bg={getFlushBadgeVariant(selectedStatement.flush_status)}
                      className="rounded-pill px-3 py-2 text-capitalize"
                    >
                      {labelize(selectedStatement.flush_status)}
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>

              {/* <Col xs={12} md={4}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Finance Reference</div>
                    <div className="fw-bold text-break">
                      {selectedStatement.finance_reference || "N/A"}
                    </div>
                  </Card.Body>
                </Card>
              </Col> */}

              <Col xs={12}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small mb-1">Remarks</div>
                    <div className="fw-bold text-break">{selectedStatement.remarks || "N/A"}</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <div className="text-center text-muted py-4">No detail available.</div>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
        .earning-statement-table thead th {
          white-space: nowrap;
        }

        .earning-view-tabs {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .earning-view-tab-btn {
          white-space: nowrap;
        }

        .earning-search-wrap {
          width: 100%;
          max-width: 280px;
          min-width: 240px;
        }

        .earning-search-input {
          border-radius: 0.5rem;
          box-shadow: none !important;
        }

        .earning-table-header {
          gap: 1rem;
        }

        @media (max-width: 575.98px) {
          .earning-view-tabs {
            width: 100%;
            margin-left: 0 !important;
          }

          .earning-view-tab-btn {
            width: 100%;
            flex: 1 1 100%;
          }

          .earning-table-header {
            flex-direction: column;
            align-items: stretch !important;
          }

          .earning-search-wrap {
            width: 100%;
            max-width: 100%;
            min-width: 100%;
          }
        }
      `}</style>
    </>
  );
}